import React from 'react';
import PropTypes from 'prop-types';
import {withRouter, Link} from 'react-router';
import {callAction} from 'client/framework';
import questionEditorActions from 'client/EditPage/V2/QuestionEditor/QuestionEditor.actions';
import questionEditorStore from 'client/EditPage/V2/QuestionEditor/QuestionEditor.store';
import NewQuestionTypeModal from 'client/EditPage/V2/NewQuestionTypeModal/NewQuestionTypeModal.react';
import {
  WithTooltip,
  Dropdown,
  DropdownItem,
  Button,
  Icon,
  copyToClipboard,
  addToast,
  Modal,
  Dialogue,
  Text,
  Toggle
} from '@albert-io/atomic';

import {QUESTIONSET_STATUSES} from 'client/constants';
import sessionStore from 'client/Session/SessionStore';
import abandonQueueChangesActions from 'client/Dennis/Content/Queue/AbandonQueueChanges.actions';
import {promptIfUnsavedOr} from 'client/Dennis/Content/Queue/Queue.react';

import ReassignQuestionSetModal from './ReassignQuestionSetModal/ReassignQuestionSetModal.react';

import {deleteQuestionSet} from './QuestionSetActions.utils';

const nLevelMap = {
  1: 'theme',
  2: 'topic',
  3: 'subtopic'
};

/**
 * Collection of actions a user can perform on a question under edit.
 *
 * If a user performs an action that will cause unsaved changes to be lost,
 * they will be prompted to save their question.
 *
 * This happens through the use of a ConfirmWillLeave component that is triggered on a route
 * change, but also manually through a WindowConfirm component, defined in the QuestionSetActions
 * render method, in situations where a route is not changing
 */
class QuestionSetActions extends React.Component {
  static propTypes = {
    isPreviewMode: PropTypes.bool,
    togglePreviewMode: PropTypes.func,
    disablePreviewMode: PropTypes.bool,
    params: PropTypes.object,
    router: PropTypes.object
  };

  constructor() {
    super();
    this.state = {
      isQuestionTypeModalActive: false,
      isSubmitForPublicationModalActive: false,
      isApproveModalActive: false,
      isReassignModalActive: false,
      showDeleteQuestionModal: false
    };
  }

  newQuestionConfirmationModalName = 'newQuestionConfirmationModalQuestionSetActions';

  makeToggleState = (stateToToggle) => {
    return () => {
      this.setState((state) => ({
        [stateToToggle]: !state[stateToToggle]
      }));
    };
  };

  toggleQuestionTypeModal = this.makeToggleState('isQuestionTypeModalActive');

  toggleReassignModal = this.makeToggleState('isReassignModalActive');

  toggleShowDeleteQuestionModal = this.makeToggleState('showDeleteQuestionModal');

  modalsByState = {
    isQuestionTypeModalActive: () => (
      <NewQuestionTypeModal
        handleClose={this.toggleQuestionTypeModal}
        handleQuestionTypeSelection={this.addQuestionToSet}
      />
    ),
    isReassignModalActive: () => (
      <ReassignQuestionSetModal
        handleClose={this.toggleReassignModal}
        handleSubmit={this.reassignQuestionSet}
        subjectId={questionEditorStore.getQuestionSet().getSubjectId()}
        currentAuthorId={questionEditorStore.getQuestionSet().getMeta().getAuthorId()}
      />
    )
  };

  getActiveModal() {
    const activeModal = Object.keys(this.modalsByState).find((stateKey) => {
      return this.state[stateKey] === true;
    });
    return activeModal ? this.modalsByState[activeModal]() : null;
  }

  submitForReview = () => {
    if (!this.canBePublished()) {
      return;
    }
    callAction(questionEditorActions.SUBMIT_FOR_REVIEW);
  };

  reassignQuestionSet = (newAuthorId) => {
    callAction(questionEditorActions.REASSIGN_QUESTION_SET, newAuthorId);
  };

  canBePublished() {
    const questionSet = questionEditorStore.getQuestionSet();
    if (!questionSet) {
      return false;
    }
    return !(
      questionSet.getAuthoringGuideLevels().isEmpty() && questionSet.getAuthoringExams().isEmpty()
    );
  }

  getSuperActions() {
    if (!sessionStore.isSuper() || !this.props.params.questionId) {
      return null;
    }
    const questionSet = questionEditorStore.getQuestionSet();
    const isPublished =
      questionEditorStore.getQuestionSet().getStatus() === QUESTIONSET_STATUSES.PUBLISHED;
    let isDuplicateAligned = false;
    const sortedGuides = questionSet.getAuthoringGuideLevels().sort((guideA, guideB) => {
      if (guideA.getNlevel() === guideB.getNlevel()) {
        isDuplicateAligned = true;
      }
      return guideA.getNlevel() - guideB.getNlevel();
    });
    let lowestGuideLevel;
    let guideSlug;
    let guideNLevel;
    let subjectSlug;
    if (!sortedGuides.isEmpty()) {
      lowestGuideLevel = sortedGuides.last();
      guideSlug = lowestGuideLevel.getUrlSlug();
      guideNLevel = lowestGuideLevel.getNlevel();
      subjectSlug = questionSet.getAuthoringSubject()?.getUrlSlug();
    }
    const isAlignmentEditorAvailable =
      !isDuplicateAligned && subjectSlug && guideSlug && isPublished;
    let alignmentTooltipText;
    if (isDuplicateAligned) {
      alignmentTooltipText = 'This question has duplicate alignment.';
    } else if (!guideSlug && !subjectSlug) {
      alignmentTooltipText = 'Must be aligned before reordering is available';
    } else if (!isPublished) {
      alignmentTooltipText = 'Must be published before reordering is available';
    }
    const isSavePending = questionEditorStore.isSavePending();
    return (
      <>
        <DropdownItem
          className='u-display_flex u-justify-content_space-between'
          withBottomBorder
          as='button'
          onClick={this.toggleReassignModal}
          disabled={isSavePending}
          icon='repeat'
        >
          Reassign
        </DropdownItem>
        <DropdownItem
          withBottomBorder
          preventDefaultClickHandler={false}
          as={Link}
          subtext={alignmentTooltipText}
          disabled={!isAlignmentEditorAvailable}
          icon='exchange'
          to={
            isAlignmentEditorAvailable && {
              pathname: `/dennis/${subjectSlug}/${guideSlug}`,
              state: {
                from: this.props.router.getCurrentLocation().pathname
              }
            }
          }
        >
          {guideNLevel ? `Reorder question in ${nLevelMap[guideNLevel]}` : 'Reorder question'}{' '}
        </DropdownItem>
      </>
    );
  }

  newQuestionHandler = () => {
    callAction(
      abandonQueueChangesActions.SET_ABANDON_CHANGES_HANDLER,
      this.toggleQuestionTypeModal
    );
    promptIfUnsavedOr(this.toggleQuestionTypeModal);
  };

  copySetId = () => {
    copyToClipboard(questionEditorStore.getQuestionSet().getId(), () => {
      addToast({
        type: 'success',
        title: 'Copied!',
        message: 'The question set identifier has been copied to your clipboard.'
      });
    });
  };

  render() {
    if (!questionEditorStore.getQuestionSet()) {
      return null;
    }
    const question = questionEditorStore.getQuestion();
    const questionSet = questionEditorStore.getQuestionSet();

    const isPreviewDisabled =
      this.props.disablePreviewMode ||
      questionEditorStore.hasErrors() ||
      (this.props.params.questionId
        ? !questionEditorStore.getQuestionQuery().isResourceReady()
        : true);

    const isSavePending = questionEditorStore.isSavePending();

    return (
      <div className='edit-page__toolbar'>
        <div className='u-mar-tb_auto'>
          <WithTooltip
            enabled={isPreviewDisabled || questionEditorStore.hasErrors()}
            content='All required fields must be filled out correctly before you can preview the
                question.'
          >
            <label
              className='guide-level-row__toggle-checkbox-toggle u-mar-l_1 u-mar-r_3'
              htmlFor={`preview-toggle-${questionSet.getId()}`}
              data-id={questionSet.getId()}
            >
              <Text emphasis='high'>Preview</Text>
              <Toggle
                size='s'
                className='u-mar-l_2'
                data-id={questionSet.getId()}
                id={`preview-toggle-${questionSet.getId()}`}
                onChange={this.props.togglePreviewMode}
                checked={this.props.isPreviewMode}
              />
            </label>
            {/* <CheckboxToggle disabled={isPreviewDisabled} label='Preview' /> */}
          </WithTooltip>
        </div>
        <div className='edit-page-controls-buttons'>
          <Dropdown
            trigger={
              <Button>
                Actions <Icon className='u-mar-l_1' icon='caret-down' />
              </Button>
            }
          >
            <DropdownItem
              withBottomBorder
              className='u-display_flex u-justify-content_space-between'
              onClick={this.copySetId}
              as='button'
              disabled={isSavePending}
              subtext={isSavePending && 'You must first save your question to copy its identifier'}
              icon='link'
            >
              Copy question set identifier
            </DropdownItem>
            {this.getSuperActions()}
            <DropdownItem
              withBottomBorder
              className='u-display_flex u-justify-content_space-between'
              onClick={this.toggleShowDeleteQuestionModal}
              icon='trash'
              iconColor='danger'
              color='red'
              as='button'
            >
              Delete
            </DropdownItem>
          </Dropdown>
        </div>
        {this.state.showDeleteQuestionModal && (
          <Modal
            ariaLabel='confirm question set deletion modal'
            handleClose={this.toggleShowDeleteQuestionModal}
          >
            {({CloseButtonWrapper, modalContentStyle}) => {
              return (
                <Dialogue
                  handleClose={this.toggleShowDeleteQuestionModal}
                  inModal
                  className={modalContentStyle}
                  title='Delete question'
                >
                  <Dialogue.Body>
                    <Text>Are you sure you want to delete this question?</Text>
                  </Dialogue.Body>
                  <Dialogue.BtnGroup>
                    <CloseButtonWrapper>
                      <Button variant='text'>Cancel</Button>
                    </CloseButtonWrapper>
                    <Button
                      color='destructive'
                      onClick={() => deleteQuestionSet(questionSet, question)}
                    >
                      Delete Question
                    </Button>
                  </Dialogue.BtnGroup>
                </Dialogue>
              );
            }}
          </Modal>
        )}
        {this.getActiveModal()}
      </div>
    );
  }
}

export default withRouter(QuestionSetActions);
